import { noop } from '@openx/utils/noop.ts';
import { useCallback, useReducer, useState } from 'react';
import { useDealContext } from '../utils/context/DealContext.tsx';
import { ActionType, type ActionTypeValue, initialActionState, type MessageActionState } from './constants.ts';

export const messageActionsReducer = (
  state: Record<string, MessageActionState>,
  action: { type: ActionTypeValue; messageId: string }
) => {
  const current = state[action.messageId];
  if (current?.feedbackSubmitted && action.type !== ActionType.THUMB_DOWN) return state;

  switch (action.type) {
    case ActionType.THUMB_UP:
      return {
        ...state,
        [action.messageId]: current?.thumbUp
          ? initialActionState
          : { thumbUp: true, thumbDown: false, requestFeedback: false, feedbackSubmitted: false },
      };
    case ActionType.THUMB_DOWN:
      return {
        ...state,
        [action.messageId]: current?.thumbDown
          ? initialActionState
          : { thumbUp: false, thumbDown: true, requestFeedback: true, feedbackSubmitted: false },
      };
    case ActionType.SUBMIT_FEEDBACK:
      return {
        ...state,
        [action.messageId]: { ...(current ?? initialActionState), requestFeedback: false, feedbackSubmitted: true },
      };
  }
};

export function useDealTroubleshootingMessageActions(dealId: string) {
  const { useDealTroubleshootingFeedback } = useDealContext();
  const { submitFeedback, resetError } = useDealTroubleshootingFeedback();
  const [actionsState, actionsDispatch] = useReducer(messageActionsReducer, {});
  const [submittingMessageId, setSubmittingMessageId] = useState<string | null>(null);

  const submitThumbUpFeedback = useCallback(
    async (langfuseTraceId: string) => {
      await submitFeedback({
        dealId,
        langfuseTraceId,
        score: true,
      });
    },
    [dealId, submitFeedback]
  );

  const submitThumbDownFeedback = useCallback(
    async (messageId: string, langfuseTraceId: string, feedbackText: string) => {
      setSubmittingMessageId(messageId);
      try {
        await submitFeedback({
          dealId,
          langfuseTraceId,
          score: false,
          comment: feedbackText,
        });
      } catch (err) {
        resetError();
        throw err;
      } finally {
        setSubmittingMessageId(null);
      }
    },
    [dealId, submitFeedback, resetError]
  );

  const handleThumbUp = useCallback(
    async (traceId: string, messageId: string) => {
      const msgState = actionsState[messageId];
      if (msgState?.feedbackSubmitted) return;
      resetError();
      if (!msgState?.thumbUp) {
        try {
          await submitThumbUpFeedback(traceId);
          actionsDispatch({ type: ActionType.THUMB_UP, messageId });
        } catch {
          resetError();
        }
      } else {
        actionsDispatch({ type: ActionType.THUMB_UP, messageId });
      }
    },
    [actionsState, resetError, submitThumbUpFeedback]
  );

  const handleThumbDown = useCallback(
    (messageId: string) => {
      resetError();
      actionsDispatch({ type: ActionType.THUMB_DOWN, messageId });
    },
    [resetError]
  );

  const handleSubmitFeedback = useCallback(
    async (messageId: string, traceId: string, feedbackText: string) => {
      try {
        await submitThumbDownFeedback(messageId, traceId, feedbackText);
        actionsDispatch({ type: ActionType.SUBMIT_FEEDBACK, messageId });
      } catch {
        noop();
      }
    },
    [submitThumbDownFeedback]
  );

  const handleCancelFeedback = useCallback(
    (messageId: string) => {
      resetError();
      actionsDispatch({ type: ActionType.THUMB_DOWN, messageId });
    },
    [resetError]
  );

  return {
    actionsState,
    submittingMessageId,
    handleThumbUp,
    handleThumbDown,
    handleSubmitFeedback,
    handleCancelFeedback,
  };
}
